import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// A JORNADA NO CRM (Douglas 21/10/2026). O estado vem calculado do backend
// (rh-jornada.service.ts); aqui só se lê e se mostra.

const API_BASE = () =>
  (import.meta.env.VITE_SUPABASE_URL as string) ??
  "https://beta.crm-api.primetecia.com.br/api";

async function jornadaRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const response = await fetch(`${API_BASE()}/v1${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${session?.access_token ?? ""}`,
      ...(init?.headers ?? {}),
    },
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(json?.error ?? `A operação falhou com HTTP ${response.status}`);
  return (json?.data ?? json) as T;
}

export type EstadoJornada =
  | "livre"
  | "aviso_almoco"
  | "almoco"
  | "aguardando_volta"
  | "aviso_fechamento"
  | "fora_expediente"
  | "liberado";

export interface JornadaAgora {
  aplica: boolean;
  estado?: EstadoJornada;
  segundos?: number | null;
  abre?: string;
  fecha?: string;
  almoco?: { inicio: string; fim: string };
  liberado_ate?: string | null;
  pode_pedir_extra: boolean;
}

export function useJornadaAgora(ativo: boolean) {
  return useQuery({
    queryKey: ["jornada", "agora"],
    enabled: ativo,
    queryFn: () => jornadaRequest<JornadaAgora>("/jornada/estado"),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
    staleTime: 10_000,
  });
}

export function useVolteiDoIntervalo() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => jornadaRequest("/jornada/voltei", { method: "POST", body: "{}" }),
    onSuccess: () => client.invalidateQueries(),
  });
}

export interface PessoaParaExtra {
  id: string;
  nome: string;
  equipe_nome: string | null;
}

export function usePessoasParaExtra(ativo: boolean) {
  return useQuery({
    queryKey: ["jornada", "pessoas-para-extra"],
    enabled: ativo,
    queryFn: () => jornadaRequest<PessoaParaExtra[]>("/jornada/pessoas-para-extra"),
    staleTime: 60_000,
  });
}

export function usePedirHoraExtra() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { usuario_ids: string[]; dia: string; inicio: string; fim: string; motivo: string }) =>
      jornadaRequest<{ id: string; status: "pendente" | "aprovada" }>("/jornada/liberacoes", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["rh", "jornada"] }),
  });
}

// ---------------------------------------------------------- aba Jornada do RH

export interface RhJornadaPessoa {
  id: string;
  nome: string;
  equipe_nome: string | null;
  primeiro_acesso: string | null;
  ultimo_acesso: string | null;
  almoco_travou_em: string | null;
  voltou_em: string | null;
  tentativas_fora: number | null;
  com_trava: boolean;
  estado: EstadoJornada | null;
  ativo_agora: boolean;
  intervalo_min: number | null;
  almoco_minutos: number | null;
}

export interface RhJornadaEquipe {
  equipe_id: string;
  nome: string;
  pessoas: number;
  almoco_inicio: string | null;
  trava_ativa: boolean;
}

export interface RhJornadaLiberacao {
  id: string;
  dia: string;
  inicio: string;
  fim: string;
  intervalo_inicio: string | null;
  intervalo_fim: string | null;
  origem: "tela" | "mcp";
  motivo: string;
  status: "pendente" | "aprovada" | "recusada" | "cancelada";
  motivo_recusa: string | null;
  pedido_por_nome: string | null;
  pedido_por_role: string | null;
  decidido_por_nome: string | null;
  decidido_em: string | null;
  pessoas: string[] | null;
  pessoas_detalhe: RhLiberacaoPessoa[];
}

export interface RhLiberacaoPessoa {
  id: string;
  nome: string;
  equipe_nome: string | null;
  primeiro_acesso: string | null;
  ultimo_acesso: string | null;
  almoco_travou_em: string | null;
  voltou_em: string | null;
}

export interface RhBarrado {
  id: string;
  nome: string;
  equipe_nome: string | null;
  tentativas_fora: number;
  ultima_tentativa_fora: string;
}

export interface PreviaLiberacao {
  ok: true;
  gravou: boolean;
  status?: "aprovada" | "pendente";
  horario: string;
  liberar: number;
  liberar_quem: Array<{ pedido: string; crm: string; equipe: string | null }>;
  nao_precisa: Array<{ pedido: string; crm: string; porque: string }>;
  ja_liberado: Array<{ pedido: string; crm: string; status: string }>;
  nao_achei: string[];
  ambiguos: Array<{ pedido: string; quem_pode_ser: string[] }>;
}

export interface PedidoLiberacaoLista {
  pessoas: string;
  usuario_ids: string[];
  dia: string;
  entrada: string;
  saida: string;
  intervalo_inicio: string;
  intervalo_fim: string;
  sem_intervalo: boolean;
  motivo: string;
  confirmar: boolean;
}

export function useRhJornadaHoje() {
  return useQuery({
    queryKey: ["rh", "jornada", "hoje"],
    queryFn: () => jornadaRequest<{ hoje: string; rows: RhJornadaPessoa[] }>("/rh/jornada/hoje"),
    refetchInterval: 60_000,
  });
}

export function useRhJornadaEquipes() {
  return useQuery({
    queryKey: ["rh", "jornada", "equipes"],
    queryFn: () => jornadaRequest<RhJornadaEquipe[]>("/rh/jornada/equipes"),
  });
}

export function useSalvarJornadaEquipe() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ equipeId, ...config }: { equipeId: string; almoco_inicio: string; trava_ativa: boolean }) =>
      jornadaRequest(`/rh/jornada/equipes/${equipeId}`, { method: "PUT", body: JSON.stringify(config) }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["rh", "jornada"] }),
  });
}

export function useRhJornadaLiberacoes() {
  return useQuery({
    queryKey: ["rh", "jornada", "liberacoes"],
    queryFn: () => jornadaRequest<RhJornadaLiberacao[]>("/rh/jornada/liberacoes"),
    refetchInterval: 60_000,
  });
}

export function useRhBarradosHoje() {
  return useQuery({
    queryKey: ["rh", "jornada", "barrados-hoje"],
    queryFn: () => jornadaRequest<RhBarrado[]>("/rh/jornada/barrados-hoje"),
    refetchInterval: 60_000,
  });
}

export function useLiberarPorLista() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PedidoLiberacaoLista) =>
      jornadaRequest<PreviaLiberacao>("/rh/jornada/liberacoes/lista", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: (r) => {
      if (r.gravou) client.invalidateQueries({ queryKey: ["rh", "jornada"] });
    },
  });
}

export function useAcrescentarNaLiberacao() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, usuario_ids }: { id: string; usuario_ids: string[] }) =>
      jornadaRequest<{ ok: true; pessoas: number }>(`/rh/jornada/liberacoes/${id}/acrescentar`, { method: "POST", body: JSON.stringify({ usuario_ids }) }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["rh", "jornada"] }),
  });
}

export function useCancelarLiberacao() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => jornadaRequest(`/rh/jornada/liberacoes/${id}/cancelar`, { method: "POST", body: "{}" }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["rh", "jornada"] }),
  });
}

export function useDecidirLiberacao() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, aprovar, motivo }: { id: string; aprovar: boolean; motivo?: string }) =>
      jornadaRequest(`/rh/jornada/liberacoes/${id}/decidir`, { method: "POST", body: JSON.stringify({ aprovar, motivo }) }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["rh", "jornada"] }),
  });
}
